<script setup lang="ts">
/**
 * TheFooter - Main footer component
 * ===================================
 * Footer with links, newsletter, and social media.
 */

import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { api } from '@/api/client'

const { t } = useI18n()

const email = ref('')
const isSubscribing = ref(false)
const subscribeMessage = ref('')
const subscribeSuccess = ref(false)

async function handleSubscribe() {
  if (!email.value.trim()) return

  isSubscribing.value = true
  subscribeMessage.value = ''

  try {
    const response = await api.post<null>('/newsletter/subscribe', {
      email: email.value
    })

    if (response.data.success) {
      subscribeSuccess.value = true
      subscribeMessage.value = t('footer.thanks_subscribe')
      email.value = ''
    } else {
      subscribeSuccess.value = false
      subscribeMessage.value = response.data.message || t('footer.subscribe_error')
    }
  } catch {
    subscribeSuccess.value = false
    subscribeMessage.value = t('footer.subscribe_error_retry')
  } finally {
    isSubscribing.value = false
  }
}

const currentYear = new Date().getFullYear()
</script>

<template>
  <footer class="bg-charcoal text-white">
    <!-- Main footer -->
    <div class="container-custom py-12 lg:py-16">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- Brand & Social -->
        <div>
          <h3 class="font-heading text-2xl text-primary-400 mb-4">
            Aura by Alexia
          </h3>
          <p class="text-gray-400 text-sm mb-6">
            {{ $t('footer.brand_description') }}
          </p>
          <!-- Social links -->
          <div class="flex space-x-4">
            <a
              href="https://instagram.com/aurabyalexia"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-primary-400 transition-colors"
              aria-label="Instagram"
            >
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a
              href="https://facebook.com/aurabyalexia"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-primary-400 transition-colors"
              aria-label="Facebook"
            >
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              href="https://pinterest.com/aurabyalexia"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-primary-400 transition-colors"
              aria-label="Pinterest"
            >
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h4 class="font-heading text-lg text-white mb-4">{{ $t('footer.links') }}</h4>
          <ul class="space-y-2">
            <li>
              <RouterLink
                :to="{ name: 'products' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('nav.shop') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'about' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('nav.about') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'blog' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('nav.blog') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'contact' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('nav.contact') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'faq' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('footer.faq') }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <!-- Customer Service -->
        <div>
          <h4 class="font-heading text-lg text-white mb-4">{{ $t('footer.customer_service') }}</h4>
          <ul class="space-y-2">
            <li>
              <RouterLink
                :to="{ name: 'shipping' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('footer.shipping_info') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'returns' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('footer.returns') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'privacy' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('footer.privacy_policy') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'terms' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('footer.terms_of_service') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'cookies' }"
                class="text-gray-400 hover:text-primary-400 transition-colors text-sm"
              >
                {{ $t('footer.cookie_policy') }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <!-- Newsletter -->
        <div>
          <h4 class="font-heading text-lg text-white mb-4">{{ $t('footer.newsletter') }}</h4>
          <p class="text-gray-400 text-sm mb-4">
            {{ $t('footer.newsletter_text') }}
          </p>
          <form @submit.prevent="handleSubscribe" class="space-y-3">
            <input
              v-model="email"
              type="email"
              :placeholder="$t('form.email')"
              class="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded
                     text-white placeholder-gray-500
                     focus:outline-none focus:border-primary-400"
              required
            />
            <button
              type="submit"
              class="w-full btn-primary"
              :disabled="isSubscribing"
            >
              {{ isSubscribing ? $t('common.loading') : $t('footer.newsletter_signup') }}
            </button>
          </form>
          <p
            v-if="subscribeMessage"
            :class="[
              'mt-2 text-sm',
              subscribeSuccess ? 'text-green-400' : 'text-red-400'
            ]"
          >
            {{ subscribeMessage }}
          </p>
        </div>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="border-t border-gray-800">
      <div class="container-custom py-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <p class="text-gray-500 text-sm">
          &copy; {{ currentYear }} Aura by Alexia. {{ $t('footer.all_rights_reserved') }}
        </p>
        <!-- Payment icons -->
        <div class="flex items-center space-x-4">
          <span class="text-gray-500 text-sm">{{ $t('footer.secure_payments') }}:</span>
          <div class="flex space-x-2">
            <!-- Visa -->
            <div class="w-10 h-6 bg-white rounded flex items-center justify-center">
              <span class="text-xs font-bold text-blue-600">VISA</span>
            </div>
            <!-- Mastercard -->
            <div class="w-10 h-6 bg-white rounded flex items-center justify-center">
              <span class="text-xs font-bold text-orange-500">MC</span>
            </div>
            <!-- PayPal -->
            <div class="w-10 h-6 bg-white rounded flex items-center justify-center">
              <span class="text-xs font-bold text-blue-800">PP</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
